const LANGUAGE_STORAGE_KEY = 'uplus_lang'
const LEGACY_LANGUAGE_STORAGE_KEY = 'language'
const OFFICIAL_SOURCE_LANGUAGE_KEY = 'uplusTradeSourceLanguage'
const OFFICIAL_SOURCE_KEY = 'uplusTradeLogoSource'
const LANGUAGE_PARAMETER_KEYS = ['lang', 'locale', 'language']
const LANGUAGE_COOKIE_KEYS = ['uplus_lang', 'language', 'lang', 'locale']

export const DEFAULT_LANGUAGE = 'zh-HK'

export const LANGUAGE_OPTIONS = Object.freeze([
  { code: 'zh-CN', apiCode: 'zh', label: '简体中文' },
  { code: 'zh-HK', apiCode: 'zh-HK', label: '繁體中文' },
  { code: 'en', apiCode: 'en', label: 'English' },
  { code: 'ko', apiCode: 'ko', label: '한국어' }
])

const LANGUAGE_ALIASES = {
  'zh': 'zh-CN',
  'zh-cn': 'zh-CN',
  'zh_cn': 'zh-CN',
  '简体中文': 'zh-CN',
  '簡體中文': 'zh-CN',
  'zh-hk': 'zh-HK',
  'zh_hk': 'zh-HK',
  'zh-tw': 'zh-HK',
  'zh_tw': 'zh-HK',
  'zh-hant': 'zh-HK',
  'zh_hant': 'zh-HK',
  '繁體中文': 'zh-HK',
  '繁体中文': 'zh-HK',
  '香港繁體': 'zh-HK',
  'en': 'en',
  'en-us': 'en',
  'en_us': 'en',
  'english': 'en',
  'ko': 'ko',
  'ko-kr': 'ko',
  'ko_kr': 'ko',
  'kn': 'ko',
  '한국어': 'ko',
  '韩文': 'ko',
  '韓文': 'ko',
  'korean': 'ko'
}

let activeLanguage = ''

function defaultLanguageOption() {
  return LANGUAGE_OPTIONS.find(item => item.code === DEFAULT_LANGUAGE) || LANGUAGE_OPTIONS[0]
}

function parameterLanguage(params) {
  for (let index = 0; index < LANGUAGE_PARAMETER_KEYS.length; index += 1) {
    const value = params.get(LANGUAGE_PARAMETER_KEYS[index])
    if (value) return value
  }
  return ''
}

function languageFromUrl(value) {
  if (!value) return ''
  try {
    const url = new URL(value, window.location.origin)
    const queryLanguage = parameterLanguage(url.searchParams)
    if (queryLanguage) return queryLanguage
    const hashQuery = String(url.hash || '').split('?')[1]
    if (hashQuery) return parameterLanguage(new URLSearchParams(hashQuery))
  } catch (error) {
    return ''
  }
  return ''
}

function incomingLanguage() {
  const queryLanguage = parameterLanguage(new URLSearchParams(window.location.search))
  if (queryLanguage) return queryLanguage

  const hashQuery = String(window.location.hash || '').split('?')[1]
  if (hashQuery) {
    const hashLanguage = parameterLanguage(new URLSearchParams(hashQuery))
    if (hashLanguage) return hashLanguage
  }

  const source = new URLSearchParams(window.location.search).get('from')
  return languageFromUrl(source)
}

function isOfficialWebsiteHost(value) {
  const host = String(value || '').trim().toLowerCase()
  return host === 'uplustrade.com' || host === 'www.uplustrade.com'
}

function isOfficialWebsiteEntry() {
  const hashPath = String(window.location.hash || '').split('?')[0]
  const source = new URLSearchParams(window.location.search).get('from')
  if (window.sessionStorage.getItem(OFFICIAL_SOURCE_KEY)) return true
  if (window.location.pathname === '/robot' || hashPath === '#/robot') return true

  try {
    if (source) {
      const sourceValue = String(source).trim()
      const sourceHost = sourceValue.replace(/^https?:\/\//i, '').split(/[/?#]/)[0]
      if (isOfficialWebsiteHost(sourceHost)) return true
    }
    if (document.referrer) {
      return isOfficialWebsiteHost(new URL(document.referrer).hostname)
    }
  } catch (error) {
    return false
  }
  return false
}

function sharedCookieLanguage() {
  if (typeof document === 'undefined' || !document.cookie) return ''
  const values = document.cookie.split(';').reduce((output, item) => {
    const separator = item.indexOf('=')
    if (separator < 0) return output
    const key = item.slice(0, separator).trim()
    const value = item.slice(separator + 1).trim()
    output[key] = value
    return output
  }, {})

  for (let index = 0; index < LANGUAGE_COOKIE_KEYS.length; index += 1) {
    const value = values[LANGUAGE_COOKIE_KEYS[index]]
    if (!value) continue
    try {
      return decodeURIComponent(value)
    } catch (error) {
      return value
    }
  }
  return ''
}

function persistLanguage(normalized) {
  const option = LANGUAGE_OPTIONS.find(item => item.code === normalized) || defaultLanguageOption()
  window.localStorage.setItem(LANGUAGE_STORAGE_KEY, option.apiCode)
  window.localStorage.setItem(LEGACY_LANGUAGE_STORAGE_KEY, option.label)
}

export function normalizeLanguage(value) {
  const source = String(value || '').trim()
  if (!source) return DEFAULT_LANGUAGE
  return LANGUAGE_ALIASES[source.toLowerCase()] || LANGUAGE_ALIASES[source] || DEFAULT_LANGUAGE
}

export function readLanguage() {
  if (activeLanguage) return activeLanguage
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE

  const entryLanguage = incomingLanguage()
  if (entryLanguage) {
    activeLanguage = normalizeLanguage(entryLanguage)
    persistLanguage(activeLanguage)
    if (isOfficialWebsiteEntry()) {
      window.sessionStorage.setItem(OFFICIAL_SOURCE_LANGUAGE_KEY, activeLanguage)
    }
    return activeLanguage
  }

  if (isOfficialWebsiteEntry()) {
    const officialLanguage = window.sessionStorage.getItem(OFFICIAL_SOURCE_LANGUAGE_KEY) ||
      languageFromUrl(document.referrer) ||
      sharedCookieLanguage()
    if (officialLanguage) {
      activeLanguage = normalizeLanguage(officialLanguage)
      persistLanguage(activeLanguage)
      return activeLanguage
    }
  }

  const current = window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
  const legacy = window.localStorage.getItem(LEGACY_LANGUAGE_STORAGE_KEY)
  activeLanguage = normalizeLanguage(current || legacy || DEFAULT_LANGUAGE)
  return activeLanguage
}

export function languageLabel(code) {
  const normalized = normalizeLanguage(code)
  const option = LANGUAGE_OPTIONS.find(item => item.code === normalized)
  return option ? option.label : defaultLanguageOption().label
}

export function languageRequestCode(code) {
  const normalized = normalizeLanguage(code)
  const option = LANGUAGE_OPTIONS.find(item => item.code === normalized)
  return option ? option.code : defaultLanguageOption().code
}

export function languageApiLocale(code) {
  const normalized = normalizeLanguage(code)
  if (normalized === 'en') return 'en_US'
  if (normalized === 'ko') return 'ko_KR'
  if (normalized === 'zh-HK') return 'zh_HK'
  return 'zh_CN'
}

export function languageIntlLocale(code = readLanguage()) {
  const normalized = normalizeLanguage(code)
  if (normalized === 'en') return 'en-US'
  if (normalized === 'ko') return 'ko-KR'
  if (normalized === 'zh-HK') return 'zh-HK'
  return 'zh-CN'
}

export function saveLanguage(code) {
  const normalized = normalizeLanguage(code)
  activeLanguage = normalized
  if (typeof window !== 'undefined') {
    persistLanguage(normalized)
    if (isOfficialWebsiteEntry()) {
      window.sessionStorage.setItem(OFFICIAL_SOURCE_LANGUAGE_KEY, normalized)
    }
    window.dispatchEvent(new CustomEvent('uplus-language-change', { detail: normalized }))
  }
  return normalized
}